import { track } from "./data-layer";

/**
 * The page's real Core Web Vitals, as the `web_vitals` event, in the shape of web.dev's
 * "vitals-ga4" recipe: `metric_name`, `value`, `metric_id`, `metric_rating` and `debug_target`.
 *
 * Only the three that decide the experience: LCP, INP and CLS. The event carries no identifier of
 * the person: the container decides, from the consent, what leaves the browser.
 */

export type ReportedMetric = {
  id: string;
  name: string;
  /** The change since the last report of this metric, in ms (a unitless score for CLS). */
  delta: number;
  rating?: "good" | "needs-improvement" | "poor";
  entries?: readonly unknown[];
};

const REPORTED = new Set(["LCP", "INP", "CLS"]);

type NodeLike = { nodeType?: number; tagName?: string; id?: string; classList?: { item(index: number): string | null } };

/** A short, readable selector for the element behind a metric: `tag#id`, `tag.firstClass` or `tag`. */
export function selectorOf(node: unknown): string | undefined {
  const element = node as NodeLike | null | undefined;
  if (element === null || element === undefined || element.nodeType !== 1 || typeof element.tagName !== "string") return undefined;

  const tag = element.tagName.toLowerCase();
  if (element.id) return `${tag}#${element.id}`;

  const firstClass = element.classList?.item(0);

  return firstClass ? `${tag}.${firstClass}` : tag;
}

/** The element a metric points at: the LCP element, the interaction's target, the first shifted node. */
export function debugTargetFor(metric: ReportedMetric): string | undefined {
  const entries = (metric.entries ?? []) as Array<{
    element?: unknown;
    target?: unknown;
    sources?: Array<{ node?: unknown }>;
  }>;

  if (metric.name === "LCP") return selectorOf(entries[entries.length - 1]?.element);
  if (metric.name === "INP") return selectorOf(entries[0]?.target);

  return selectorOf(entries.find((entry) => entry.sources?.[0]?.node !== undefined)?.sources?.[0]?.node);
}

/**
 * A stable reference, on purpose: `useReportWebVitals` re-reports everything it has when handed a
 * new function.
 */
export function reportWebVital(metric: ReportedMetric): void {
  if (!REPORTED.has(metric.name)) return;

  const debugTarget = debugTargetFor(metric);

  track({
    event: "web_vitals",
    metric_name: metric.name as "LCP" | "INP" | "CLS",
    // Whole numbers, as GA4 wants them: CLS is a small score, so it is scaled by a thousand.
    value: Math.round(metric.name === "CLS" ? metric.delta * 1000 : metric.delta),
    metric_id: metric.id,
    metric_rating: metric.rating ?? "good",
    ...(debugTarget === undefined ? {} : { debug_target: debugTarget }),
  });
}
